// Sales (CRM, Signals, Meeting prep): typed routes. Verified against
// src/aithora/v3/sales.ts (mounted on /api/aithora; Sales staff only, Partner reads) and
// src/crm/router.ts (mounted on /api/crm; the same shared workspace, the store enforces
// deal ownership and the stage gates on every path). The /api/crm answers carry no
// honesty flag, so those routes are not `honest`.
import type { ApiClient } from './client';
import type { ConsentOnFile } from './connections-routes';
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type {
  BulkStartResponse,
  CatalogParseResponse,
  CatalogSearchBody,
  CatalogSearchResponse,
  CrmActivitiesResponse,
  CrmActivity,
  CrmActivityBody,
  CrmCompanyRecord,
  CrmContact,
  CrmContactsCount,
  CrmContactsResponse,
  CrmCounts,
  CrmCustomerHealthResponse,
  CrmDeal,
  CrmDealsResponse,
  CrmMember,
  CrmPipeline,
  CrmProspectsResponse,
  CrmSavedView,
  CrmBulkBody,
  CrmBulkResponse,
  CrmBulkUndoResponse,
  CrmDealPatch,
  CrmHistoryResponse,
  GmailAccountsResponse,
  LogOutcomeBody,
  MailSettings,
  MailVisibility,
  MailWaitingResponse,
  OperatorSortKey,
  ThreadSummary,
  ThreadSummaryResponse,
  LogOutcomeResponse,
  MailQueueResponse,
  MeetingPrepResponse,
  CrmMyDayResponse,
  CrmOperatorsResponse,
  RepActivity,
  SalesKitRowsResponse,
  SalesSignalsResponse,
  StageCountsResponse,
} from './sales-types';

const A = AITHORA_PREFIX;
const C = '/api/crm';
type ScopeQ = { scope?: 'mine' | 'team'; tz?: string };

// ── /api/aithora (v3/sales.ts) ──────────────────────────────────────────────────

/** GET /crm/signals?scope=mine|team&tz=: server-computed Signals (polled; no push channel yet). */
export const salesSignals = route<SalesSignalsResponse, ScopeQ>()('GET', `${A}/crm/signals`, { honest: true });
/** GET /crm/my-day?scope=mine|team&tz=: the strip, the pipeline flow, Today and Not started yet. */
export const crmMyDay = route<CrmMyDayResponse, ScopeQ>()('GET', `${A}/crm/my-day`, { honest: true });
/** GET /crm/deals/stage-counts?scope=mine|team (Lost included). */
export const crmStageCounts = route<StageCountsResponse, ScopeQ>()('GET', `${A}/crm/deals/stage-counts`, { honest: true });
/** GET /crm/contacts/count */
export const crmContactsCount = route<CrmContactsCount>()('GET', `${A}/crm/contacts/count`, { honest: true });
/** GET /crm/counts: every rail count. */
export const crmCounts = route<CrmCounts>()('GET', `${A}/crm/counts`, { honest: true });
/** POST /crm/deals/:id/snooze {days | until}: the owner or a manager. */
export const crmDealSnooze = route<{ real: boolean; deal_id: string; snoozed_until: string | null }, void, { days?: number; until?: string | null }>()('POST', `${A}/crm/deals/:id/snooze`, { honest: true });
/** POST /crm/deals/:id/reassign {ownerId}: managers; a rep may claim an unowned deal. */
export const crmDealReassign = route<{ real: boolean; deal: CrmDeal }, void, { ownerId: string | null }>()('POST', `${A}/crm/deals/:id/reassign`, { honest: true });
/** POST /crm/deals/bulk-start {facilityIds[], track?, source?}: a Lead deal for each facility with no open deal. */
export const crmBulkStart = route<BulkStartResponse, void, { facilityIds: string[]; track?: 'A' | 'B'; source?: string }>()('POST', `${A}/crm/deals/bulk-start`, { honest: true });
/** POST /crm/facilities/search {filters, sort, cursor, limit=40}: the catalog, filtered and counted on the server. */
export const crmFacilitySearch = route<CatalogSearchResponse, void, CatalogSearchBody>()('POST', `${A}/crm/facilities/search`, { honest: true, readOnlySafe: true });
/** POST /crm/facilities/parse {text}: plain English to the same filter object (chips). */
export const crmFacilityParse = route<CatalogParseResponse, void, { text: string }>()('POST', `${A}/crm/facilities/parse`, { honest: true, readOnlySafe: true });
/** GET /crm/facilities/facets: every state, type and (discovery-captured) EHR in the catalog, for the filter menus. */
export const crmFacilityFacets = route<{ real: boolean; state: string[]; type: string[]; ehr: string[] }>()('GET', `${A}/crm/facilities/facets`, { honest: true });
/** POST /crm/facilities/export {filters, sort} | {facilityIds[]}: CSV (read with client.raw). */
export const CRM_FACILITY_EXPORT_PATH = `${A}/crm/facilities/export`;
/** GET /crm/companies/operators?q&state&kind&sort&dir&offset&limit: Operators & owners, rolled up and sorted on the server. */
export const crmOperators = route<CrmOperatorsResponse, { q?: string; state?: string; kind?: string; sort?: OperatorSortKey; dir?: 'asc' | 'desc'; offset?: number; limit?: number }>()('GET', `${A}/crm/companies/operators`, { honest: true });
/** GET /crm/mail-queue: unmatched deals@ / partner@ mail, never guessed. */
export const crmMailQueue = route<MailQueueResponse>()('GET', `${A}/crm/mail-queue`, { honest: true });
/** POST /crm/mail-queue/:id/link {facilityId | companyId} */
export const crmMailLink = route<{ real: boolean; activities: string[] }, void, { facilityId?: string; companyId?: string }>()('POST', `${A}/crm/mail-queue/:id/link`, { honest: true });
/** POST /crm/mail-queue/:id/dismiss: "Don't log". */
export const crmMailDismiss = route<{ real: boolean; dismissed: string }, void, Record<string, never>>()('POST', `${A}/crm/mail-queue/:id/dismiss`, { honest: true });
/** POST /crm/facilities/:facilityId/call {notes?, dealId?, contactId?}: a Call activity by this rep. */
export const crmFacilityCall = route<{ real: boolean; activity: CrmActivity }, void, { notes?: string; subject?: string; dealId?: string; contactId?: string }>()('POST', `${A}/crm/facilities/:facilityId/call`, { honest: true });

/** GET /sales/prep/:facilityId: built per request, never stored. */
export const salesPrep = route<MeetingPrepResponse>()('GET', `${A}/sales/prep/:facilityId`, { honest: true });
/** POST /sales/prep/:facilityId/log-outcome: a Meeting activity; a facility with no deal gets one at Lead. */
export const salesLogOutcome = route<LogOutcomeResponse, void, LogOutcomeBody>()('POST', `${A}/sales/prep/:facilityId/log-outcome`, { honest: true });
/** GET /sales/kit: the Sales Kit rows; the meeting prep resolves its dq./pb. keys through them. */
export const salesKitRowsForPrep = route<SalesKitRowsResponse, { doc?: string }>()('GET', `${A}/sales/kit`, { honest: true });

/** GET /crm/mail/settings: my mailbox visibility, never-sync list and connected accounts. */
export const crmMailSettings = route<MailSettings>()('GET', `${A}/crm/mail/settings`, { honest: true });
/** PUT /crm/mail/settings {visibility}: what teammates see of my synced mail. */
export const crmMailSettingsSave = route<MailSettings, void, { visibility: MailVisibility }>()('PUT', `${A}/crm/mail/settings`, { honest: true });
/** POST /crm/mail/never-sync {entry}: a domain or an address that never syncs. */
export const crmNeverSyncAdd = route<MailSettings, void, { entry: string }>()('POST', `${A}/crm/mail/never-sync`, { honest: true });
/** DELETE /crm/mail/never-sync/:entry */
export const crmNeverSyncRemove = route<MailSettings>()('DELETE', `${A}/crm/mail/never-sync/:entry`, { honest: true });
/** POST /crm/mail/threads/:threadKey/private {private}: only the rep whose mailbox holds the thread. */
export const crmMailThreadPrivate = route<{ real: boolean; thread_key: string; private: boolean }, void, { private: boolean }>()('POST', `${A}/crm/mail/threads/:threadKey/private`, { honest: true });
/** GET /crm/mail/waiting: they have not replied in 5 days, or I have not in 2. */
export const crmMailWaiting = route<MailWaitingResponse>()('GET', `${A}/crm/mail/waiting`, { honest: true });
/** POST /crm/mail/waiting/:threadKey/snooze {days=2} */
export const crmMailWaitingSnooze = route<{ real: boolean; thread_key: string; snoozed_until: string | null; done: boolean }, void, { days?: number }>()('POST', `${A}/crm/mail/waiting/:threadKey/snooze`, { honest: true });
/** POST /crm/mail/waiting/:threadKey/done */
export const crmMailWaitingDone = route<{ real: boolean; thread_key: string; snoozed_until: string | null; done: boolean }, void, Record<string, never>>()('POST', `${A}/crm/mail/waiting/:threadKey/done`, { honest: true });
/** GET /crm/deals/:id/thread-summary: the latest Atlas draft for the deal's thread. */
export const crmThreadSummary = route<ThreadSummaryResponse>()('GET', `${A}/crm/deals/:id/thread-summary`, { honest: true });
/** POST /crm/deals/:id/thread-summary {threadKey?}: draft one now. 422 on health information, 503 when the model is unavailable. */
export const crmThreadSummaryDraft = route<{ real: boolean; summary: ThreadSummary }, void, { threadKey?: string }>()('POST', `${A}/crm/deals/:id/thread-summary`, { honest: true, acceptStatuses: [503] });
/** POST /crm/thread-summaries/:id/accept: becomes a task; sealed to the Truth Ledger. */
export const crmThreadSummaryAccept = route<{ real: boolean; summary: ThreadSummary; ledger_entry_id: string }, void, Record<string, never>>()('POST', `${A}/crm/thread-summaries/:id/accept`, { honest: true });
/** POST /crm/thread-summaries/:id/dismiss: sealed to the Truth Ledger. */
export const crmThreadSummaryDismiss = route<{ real: boolean; summary: ThreadSummary; ledger_entry_id: string }, void, Record<string, never>>()('POST', `${A}/crm/thread-summaries/:id/dismiss`, { honest: true });

/** GET /reps/:id/activity?days=7 ("me" = the signed-in rep). */
export const repActivity = route<RepActivity, { days?: number }>()('GET', `${A}/reps/:id/activity`, { honest: true });

// ── /api/crm (crm/router.ts) ───────────────────────────────────────────────────

/** GET /pipelines: Track A (the default Facility board) and Track B (Multi-site), with stages. */
export const crmPipelines = route<{ pipelines: CrmPipeline[] }>()('GET', `${C}/pipelines`);
/** GET /deals?status&ownerId&facilityId&pipelineId&q&limit&offset */
export const crmDeals = route<CrmDealsResponse, { status?: string; ownerId?: string; facilityId?: string; pipelineId?: string; q?: string; limit?: number; offset?: number }>()('GET', `${C}/deals`);
/** POST /deals: a new deal (track A|B, source e.g. catalog / territory). */
export const crmDealCreate = route<{ deal: CrmDeal }, void, { name: string; facilityId?: string | null; type?: string; track?: 'A' | 'B'; source?: string; pipelineId?: string }>()('POST', `${C}/deals`);
/** PATCH /deals/:id (nextMeetingAt, amountCents, ...): the owner or a manager. */
export const crmDealUpdate = route<{ deal: CrmDeal }, void, CrmDealPatch>()('PATCH', `${C}/deals/:id`);
/**
 * POST /deals/:id/move {stageId, lostReason?}: the stage gates run on the server. 422 with
 * the gate's sentence when a deal tries to leave Lead with no Email, Call or Meeting, or to
 * go Lost without one of the fixed reasons; 403 when the caller does not own the deal.
 */
export const crmDealMove = route<{ deal: CrmDeal }, void, { stageId: string; lostReason?: string }>()('POST', `${C}/deals/:id/move`);
/** POST /deals/:id/lost {lostReason}: one of No budget | Chose another vendor | Not the right time | No response. */
export const crmDealLost = route<{ deal: CrmDeal }, void, { lostReason: string }>()('POST', `${C}/deals/:id/lost`);
/** GET /activities?dealId|facilityId|contactId&type&limit */
export const crmActivities = route<CrmActivitiesResponse, { dealId?: string; facilityId?: string; contactId?: string; type?: string; limit?: number; offset?: number }>()('GET', `${C}/activities`);
/** POST /activities: a note, call, email, task or meeting (a Meeting needs occurredAt). */
export const crmActivityCreate = route<{ activity: CrmActivity }, void, CrmActivityBody>()('POST', `${C}/activities`);
/** POST /activities/:id/complete {complete?} */
export const crmActivityComplete = route<{ activity: CrmActivity }, void, { complete?: boolean }>()('POST', `${C}/activities/:id/complete`);
/** GET /contacts?q&role&facilityId&limit&offset */
export const crmContacts = route<CrmContactsResponse, { q?: string; role?: string; facilityId?: string; limit?: number; offset?: number }>()('GET', `${C}/contacts`);
/** POST /contacts */
export const crmContactCreate = route<{ contact: CrmContact }, void, { name: string; email?: string; phone?: string; title?: string; role?: string; facilityId?: string | null }>()('POST', `${C}/contacts`);
/** GET /contacts/:id */
export const crmContact = route<{ contact: CrmContact }>()('GET', `${C}/contacts/:id`);
/** PATCH /contacts/:id */
export const crmContactUpdate = route<{ contact: CrmContact }, void, { name?: string; email?: string | null; phone?: string | null; title?: string | null }>()('PATCH', `${C}/contacts/:id`);
/** The sales-text consent on file for a contact's number (TCPA; src/aithora/contact-consent.ts).
 *  No CRM text goes without it, and never after a STOP. */
export interface CrmSmsConsent {
  /** The contact's number on file, or null when none. */
  phone: string | null;
  consent: ConsentOnFile;
  /** The number replied STOP: nothing can be recorded over it. */
  optedOut: boolean;
  /** The default words offered (the rep records what the prospect actually agreed to). */
  wording: string;
}
/** GET /contacts/:id/sms-consent */
export const crmContactSmsConsent = route<CrmSmsConsent>()('GET', `${C}/contacts/:id/sms-consent`);
/** POST /contacts/:id/sms-consent { source, wording, granted?, note? }: record the consent the
 *  prospect gave (or its withdrawal) for the contact's number; audited. 409 after a STOP. */
export const crmContactSmsConsentRecord = route<CrmSmsConsent, void, { source: 'verbal' | 'paper' | 'web_form'; wording: string; granted?: boolean; note?: string }>()(
  'POST', `${C}/contacts/:id/sms-consent`);
/** POST /contacts/:id/archive: soft delete (kept, hidden from lists and counts). */
export const crmContactArchive = route<{ ok: true }, void, Record<string, never>>()('POST', `${C}/contacts/:id/archive`);
/** POST /contacts/import {csv}: header name,email,phone,title,role,facilityId; de-duplicated by email. */
export const crmContactsImport = route<{ created?: number; updated?: number; skipped?: number }, void, { csv: string }>()('POST', `${C}/contacts/import`);
/** GET /contacts/export: CSV (read with client.raw). */
export const CRM_CONTACTS_EXPORT_PATH = `${C}/contacts/export`;
// Restored from the production CRM (crm_view.dart), finish-1:
/** POST /contacts/:id/restore: bring an archived contact back. 404 when it was not archived. */
export const crmContactRestore = route<{ ok: true }, void, Record<string, never>>()('POST', `${C}/contacts/:id/restore`);
/** POST /deals/:id/archive: soft delete a deal (kept, hidden). */
export const crmDealArchive = route<{ ok: true }, void, Record<string, never>>()('POST', `${C}/deals/:id/archive`);
/** POST /deals/:id/restore: bring an archived deal back. */
export const crmDealRestore = route<{ ok: true }, void, Record<string, never>>()('POST', `${C}/deals/:id/restore`);
/** POST /deals/:id/contacts {contactId, role?}: link a person to a deal. */
export const crmDealContactLink = route<{ link: unknown }, void, { contactId: string; role?: string }>()('POST', `${C}/deals/:id/contacts`);
/** POST /bulk/reassign {entity, ids, ownerId}: give records to one owner. */
export const crmBulkReassign = route<{ ok: true; count: number }, void, { entity: 'deals' | 'contacts' | 'companies'; ids: string[]; ownerId: string | null }>()(
  'POST', `${C}/bulk/reassign`);
/** POST /companies/import {csv}: track catalog facilities by id, ccn or npi column. */
export const crmCompaniesImport = route<{ inserted?: number; updated?: number; skipped?: number; errors?: unknown[] }, void, { csv: string }>()(
  'POST', `${C}/companies/import`);
/** GET /deals/export?status=: CSV (read with client.raw). */
export const CRM_DEALS_EXPORT_PATH = `${C}/deals/export`;
/** GET /companies/:facilityId: the catalog row with the CRM overlay, live scoring and open dollars. */
export const crmCompany = route<CrmCompanyRecord>()('GET', `${C}/companies/:facilityId`);
/** GET /customer-health: linked customers with their health band. */
export const crmCustomerHealth = route<CrmCustomerHealthResponse>()('GET', `${C}/customer-health`);
/** GET /prospects?limit: the prioritized call list (money on the table x winnability). */
export const crmProspects = route<CrmProspectsResponse, { limit?: number; state?: string; type?: string }>()('GET', `${C}/prospects`);
/** GET /members: the assignable owners (Aithora team). */
export const crmMembers = route<{ members: CrmMember[] }>()('GET', `${C}/members`);
/** GET /views?object=company|contact|deal: own and shared saved views. */
export const crmViews = route<{ views: CrmSavedView[] }, { object: 'company' | 'contact' | 'deal' }>()('GET', `${C}/views`);
/** POST /views {object, name, filters, scope: Mine | Team, sort} */
export const crmViewCreate = route<{ view: CrmSavedView }, void, { object: 'company' | 'contact' | 'deal'; name: string; filters: Record<string, unknown>; scope: 'Mine' | 'Team'; sort?: unknown; columns?: string[] }>()('POST', `${C}/views`);
// crm-extras (served, src/crm/router.ts + src/crm/bulk.ts):
/** PATCH /views/:id: the owner (or a manager, for a Team view) saves changes. 403 otherwise. */
export const crmViewUpdate = route<{ view: CrmSavedView }, void, { name?: string; filters?: Record<string, unknown>; columns?: string[]; scope?: 'Mine' | 'Team'; sort?: unknown }>()('PATCH', `${C}/views/:id`);
/** DELETE /views/:id: the owner (or a manager, for a Team view). */
export const crmViewDelete = route<{ ok: true }>()('DELETE', `${C}/views/:id`);
/** PUT /views/default {object, viewId | null}: this person's default view for an object. */
export const crmViewDefault = route<{ ok: true; object: string; viewId: string | null }, void, { object: 'company' | 'contact' | 'deal'; viewId: string | null }>()('PUT', `${C}/views/default`);
/** POST /bulk: one transaction, at most 500 records, a result per record, audited per record. */
export const crmBulk = route<CrmBulkResponse, void, CrmBulkBody>()('POST', `${C}/bulk`);
/** POST /bulk/:opId/undo: the author puts a bulk change back (rows changed since are left). */
export const crmBulkUndo = route<CrmBulkUndoResponse, void, Record<string, never>>()('POST', `${C}/bulk/:opId/undo`);
/** The company statuses (production crm_view.dart _statuses; src/crm/bulk.ts COMPANY_STATUSES). */
export const CRM_COMPANY_STATUSES = ['prospect', 'qualified', 'contacted', 'pilot', 'customer', 'churned', 'disqualified'] as const;
/** The bulk cap the server enforces (src/crm/bulk.ts BULK_CAP). */
export const CRM_BULK_CAP = 500;
/** GET /history?entityId=: who changed this record, newest first. */
export const crmHistory = route<CrmHistoryResponse, { entityId: string; limit?: number }>()('GET', `${C}/history`);
/** GET /gmail/accounts: this workspace's connected mailboxes (no tokens). */
export const crmGmailAccounts = route<GmailAccountsResponse>()('GET', `${C}/gmail/accounts`);
/** GET /gmail/connect: the Google sign-in URL (503 when the integration is not configured). */
export const crmGmailConnect = route<{ url: string }>()('GET', `${C}/gmail/connect`);
/** DELETE /gmail/:id: disconnect a mailbox. */
export const crmGmailDisconnect = route<{ ok: true }>()('DELETE', `${C}/gmail/:id`);

/** Download a CSV the server builds (the export routes answer text/csv, not JSON). */
export async function downloadCsv(client: ApiClient, path: string, filename: string, init: { method?: 'GET' | 'POST'; body?: unknown } = {}): Promise<void> {
  const res = await client.raw(path, {
    method: init.method ?? 'GET',
    headers: init.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
